import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApiSend } from '../../utils/httpClientTs';
import {
  createNotice,
  type CreateNoticeRequest,
} from '../../endpoints/notices';
import type { GroupPermission } from '../../types/group';
import GroupPermissionsField from '../../Components/GroupPermissionsField';
import AlertItem from '../../Components/AlertItem';

const NoticeManagementCreate: React.FC = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [recipientPermissions, setRecipientPermissions] = useState<
    GroupPermission[]
  >([]);
  const [alertState, setAlertState] = useState<{
    severity: 'success' | 'error' | 'warning' | 'info';
    description: string;
    title?: string;
  } | null>(null);

  const { mutate: createMutate, isPending: isSaving } = useApiSend(
    (body: CreateNoticeRequest) => createNotice(body),
    () => {
      setAlertState({
        severity: 'success',
        description: 'Notice created. Recipients will receive it shortly.',
      });
      navigate('/administration/notice-management');
    },
    (err: Error) => {
      setAlertState({
        severity: 'error',
        description:
          (err as any)?.response?.data?.error ||
          (err as any)?.message ||
          'Create failed.',
      });
    },
    [['notices']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setAlertState({ severity: 'error', description: 'Title is required.' });
      return;
    }
    if (!description.trim()) {
      setAlertState({
        severity: 'error',
        description: 'Description is required.',
      });
      return;
    }
    if (recipientPermissions.length === 0) {
      setAlertState({
        severity: 'error',
        description: 'Select at least one recipient group type.',
      });
      return;
    }
    createMutate({
      title: title.trim(),
      description: description.trim(),
      link_url: linkUrl.trim() || undefined,
      expiry_date: expiryDate || new Date().toISOString().slice(0, 10),
      recipient_group_type_ids: recipientPermissions.map(
        (p) => p.group_type_id
      ),
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Create Notice
        </Typography>
      </Grid>
      {alertState && (
        <Grid item xs={12}>
          <AlertItem
            severity={alertState.severity}
            description={alertState.description}
            title={alertState.title}
            onClose={() => setAlertState(null)}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  multiline
                  rows={3}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Link URL (optional)"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  size="small"
                  placeholder="https://..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Expiry date"
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <GroupPermissionsField
                  value={recipientPermissions}
                  onChange={setRecipientPermissions}
                  enableRoleSelection={false}
                  requireSelection
                  buttonLabel="Select recipient group types"
                  helperText="Choose which group types will receive this notice."
                  emptyHint="Select at least one group type."
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() =>
                      navigate('/administration/notice-management')
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={16} /> : null}
                  >
                    {isSaving ? 'Creating…' : 'Create Notice'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default NoticeManagementCreate;
